import * as XLSX from "xlsx";
import {
  expandSentinels, filterSelected, isSelectAll, resolveMulti,
} from "Components/SbJdCreationDrawerV2/helpers";

export const BATCH_LIMIT = 50;

export const TEMPLATE_SHEET = 'JD Upload Template';

// Hidden template sheet whose row 1 holds each column's field key
export const FIELD_KEYS_SHEET = 'FIELD_KEYS';

// Hidden sheets the template's VLOOKUP formulas read from
export const JOB_LOOKUP_SHEET = 'LOOKUP';
export const REPORTING_TO_LOOKUP_SHEET = 'REPORTING_TO_LOOKUP';

// Template keys filled by a Job Code VLOOKUP → their LOOKUP sheet header
const JOB_LOOKUP_KEYS = {
  alt_role_title: 'standard_name',
  legal_entity: 'legal_entity',
  business_unit: 'business_unit',
  role_type: 'role_type',
  level: 'level',
  work_environment: 'work_environment',
};

// Maps template field keys → internal row field keys. Read by key, not header
// text, since headers show org-specific labels (e.g. "Company" for legal_entity).
export const EXCEL_COLUMN_MAP = {
  job_code: 'jobCode',
  alt_role_title: 'standardIndustryName',
  role: 'jobTitle',
  legal_entity: 'legalEntity',
  business_unit: 'businessUnit',
  function: 'functionDept',
  sub_function: 'subFunction',
  specialty: 'specialty',
  role_type: 'roleType',
  level: 'jobLevel',
  work_environment: 'workEnvironment',
  location: 'location',
  role_context: 'roleContext',
  // role_reporting_to (display text) is human-readable only; the manager JD's
  // sb_unique_id comes from its hidden lookup column
  is_apex_role: 'isApexRole',
  role_reporting_to_sb_unique_id: 'roleReportingToSbUniqueId',
};

export const EMPTY_ROW = {
  source: 'manual',
  jobCode: null, standardIndustryName: '',
  jobTitle: '', legalEntity: null, businessUnit: null,
  functionDept: null, subFunction: null, specialty: [], invalidSpecialty: null, roleType: null,
  jobLevel: null, workEnvironment: null, location: null,
  file: null, fileError: null, roleContext: null,
  roleReportingToSbUniqueId: null, isApexRole: false,
};

// Fields cleared when a parent org-structure field changes
export const CASCADE_RESETS = {
  legalEntity: { businessUnit: null, functionDept: null, subFunction: null, specialty: [], invalidSpecialty: null, location: null },
  businessUnit: { functionDept: null, subFunction: null, specialty: [], invalidSpecialty: null },
  functionDept: { subFunction: null, specialty: [], invalidSpecialty: null },
  subFunction: { specialty: [], invalidSpecialty: null },
};

export const getCascade = (orgStructure, legalEntity, businessUnit, functionDept, subFunction) => {
  const entity = orgStructure?.find(e => e.legal_entity === legalEntity);
  const allBUs = entity?.business_units || [];
  const uniqueBUs = [...new Map(allBUs.map(b => [b.business_unit, b])).values()];
  const selectedBU = allBUs.find(b => b.business_unit === businessUnit);
  const selectedFunc = selectedBU?.functions?.find(f => f.function === functionDept);
  const selectedSF = selectedFunc?.sub_functions?.find(sf => sf.sub_function === subFunction);
  return {
    businessUnits: uniqueBUs.map(b => ({ value: b.business_unit, label: b.business_unit })),
    functions: (selectedBU?.functions || []).map(f => ({ value: f.function, label: f.function })),
    subFunctions: (selectedFunc?.sub_functions || []).map(sf => ({ value: sf.sub_function, label: sf.sub_function })),
    specialties: (selectedSF?.specialties || []).map(s => ({ value: s.specialty, label: s.specialty })),
    locations: (entity?.locations || []).map(l => ({ value: l.name, label: l.name })),
  };
};

// True when the options hold at least one real specialty (not only "ALL - All")
export const hasRealSpecialties = (specialties) => specialties.some(o => !isSelectAll(o.value));

// Specialty is required only when the row's sub-function has real specialties (same as backend).
// Empty arrays (multi-select fields like specialty) count as missing.
export const getMissingFields = (row, requiredFields, orgStructure) =>
  requiredFields
    .filter((f) => f.key !== 'specialty' || hasRealSpecialties(
      getCascade(orgStructure, row.legalEntity, row.businessUnit, row.functionDept, row.subFunction).specialties))
    .filter((f) => !row[f.key] || (Array.isArray(row[f.key]) && !row[f.key].length))
    .map((f) => f.label);

// Comma-joined specialty text → valid options. Any unknown value returns [] and the
// caller flags the row so the user re-picks.
export const resolveSpecialty = (text, options) => {
  const normalize = (s) => String(s || '').split(',').map(p => p.trim()).filter(Boolean).join(',');
  const resolved = resolveMulti(String(text || '').trim(), options.map(o => o.value));
  const valid = filterSelected(resolved, options);
  if (valid.length !== resolved.length || normalize(resolved.join(',')) !== normalize(text)) return [];
  return expandSentinels(valid, options);
};

// Case-insensitive key, same as Excel's VLOOKUP
const lookupKey = (value) => String(value ?? '').trim().toLowerCase();

// Reads a lookup sheet into key → { header: value } using its row 1 headers.
// Keeps the first row for a repeated key, same as VLOOKUP.
const readLookup = (sheet, keyHeader) => {
  const lookup = new Map();
  if (!sheet) return lookup;
  const [headers = [], ...rows] = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
  const keyIdx = headers.indexOf(keyHeader);
  if (keyIdx < 0) return lookup;
  rows.forEach(r => {
    const key = lookupKey(r[keyIdx]);
    if (!lookup.has(key)) lookup.set(key, Object.fromEntries(headers.map((h, i) => [h, r[i]])));
  });
  return lookup;
};

// Reads template rows by the FIELD_KEYS sheet. Returns null for an outdated template (no FIELD_KEYS).
// Formula cells read blank if the file was never saved in Excel, so they fall back to the file's lookup sheets.
export const parseTemplateRows = (workbook, { orgStructure, resolveJobLevel }) => {
  const sheetName = workbook.SheetNames.includes(TEMPLATE_SHEET) ? TEMPLATE_SHEET : workbook.SheetNames[0];
  if (!workbook.SheetNames.includes(TEMPLATE_SHEET)) {
    console.warn(`[BulkJdUpload] Sheet "${TEMPLATE_SHEET}" not found, falling back to "${sheetName}"`);
  }
  const keysSheet = workbook.Sheets[FIELD_KEYS_SHEET];
  if (!keysSheet) return null;

  const [columnKeys = []] = XLSX.utils.sheet_to_json(keysSheet, { header: 1, defval: '' });
  const aoaRows = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { header: 1, range: 1, defval: '' });
  const mappedFieldKeys = Object.values(EXCEL_COLUMN_MAP);
  const jobLookup = readLookup(workbook.Sheets[JOB_LOOKUP_SHEET], 'job_code');
  const reportingToLookup = readLookup(workbook.Sheets[REPORTING_TO_LOOKUP_SHEET], 'display');
  const cellText = (cells, key) => String(cells[columnKeys.indexOf(key)] ?? '').trim();

  return aoaRows
    .slice(0, BATCH_LIMIT)
    .map((cells, idx) => {
      const rowData = { ...EMPTY_ROW, source: 'excel', key: `excel-${idx}-${Date.now()}` };
      const jobCode = cellText(cells, 'job_code');
      const reportingTo = cellText(cells, 'role_reporting_to');
      const jobRow = jobCode ? jobLookup.get(lookupKey(jobCode)) : null;
      const managerRow = reportingTo ? reportingToLookup.get(lookupKey(reportingTo)) : null;
      const lookupValue = (columnKey) => (columnKey === 'role_reporting_to_sb_unique_id'
        ? managerRow?.sb_unique_id
        : jobRow?.[JOB_LOOKUP_KEYS[columnKey]]);
      columnKeys.forEach((columnKey, colIdx) => {
        const field = EXCEL_COLUMN_MAP[columnKey];
        if (!field) return;
        const cellVal = cells[colIdx] ?? '';
        const val = cellVal !== '' ? cellVal : (lookupValue(columnKey) ?? '');
        if (field === 'jobLevel') {
          rowData.jobLevel = resolveJobLevel(val);
        } else if (field === 'isApexRole') {
          rowData.isApexRole = String(val).trim().toLowerCase() === 'true';
        } else {
          rowData[field] = val !== '' ? String(val).trim() : null;
        }
      });
      // Specialty is comma-joined in Excel; resolve against this row's sub-function options
      const { specialties } = getCascade(orgStructure, rowData.legalEntity, rowData.businessUnit, rowData.functionDept, rowData.subFunction);
      const specialtyText = rowData.specialty;
      rowData.specialty = resolveSpecialty(specialtyText, specialties);
      // Keep the unknown text so the row can show a warning instead of dropping it silently
      rowData.invalidSpecialty = specialtyText && !rowData.specialty.length ? specialtyText : null;
      // An apex role has no manager
      if (rowData.isApexRole) rowData.roleReportingToSbUniqueId = null;
      return rowData;
    })
    .filter(row =>
      mappedFieldKeys.some(k => k !== 'isApexRole' && row[k] !== null && row[k] !== ''
        && !(Array.isArray(row[k]) && !row[k].length))
      || row.isApexRole
      || row.roleReportingToSbUniqueId
    );
};
